import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCheck, Loader2, Undo2 } from "lucide-react";

/** PROMPT L31: "Resolver todas" (Acciones) y "Descartar todas" (Respuestas). El servidor cuenta y actúa
 * sobre TODAS las pendientes (no sobre las 500 visibles); aquí solo se muestra la vista previa y se confirma. */
export const MODOS = {
  acciones: {
    base: "/captura/acciones/resolver-todas", verbo: "resolver", titulo: "Resolver todas", conUrgentes: true,
    nota: "No se envía nada a los clientes.", hecho: (d) => `${d.resueltas} correo(s) resuelto(s).`,
  },
  respuestas: {
    base: "/respuestas/descartar-todas", verbo: "descartar", titulo: "Descartar todas", conUrgentes: false,
    nota: "No se envía nada. Los borradores quedan descartados.", hecho: (d) => `${d.descartadas} borrador(es) descartado(s).`,
  },
  revisar: {
    base: "/captura/bandeja/quitar-no-reservas", verbo: "quitar", titulo: "Quitar lo que no es reserva", conUrgentes: false,
    nota: "Saca de A revisar solo lo que no es una reserva (agradecimientos, promociones, mensajes sin datos). No confirma ni cancela ninguna reserva y no envía nada.",
    hecho: (d) => `${d.quitadas} quitada(s) de A revisar.`,
  },
};

const n = (x) => (x || 0).toLocaleString("es");

export function MasivoDialog({ open, onOpenChange, onDone, modo }) {
  const cfg = MODOS[modo];
  const [vista, setVista] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [excluirUrgentes, setExcluirUrgentes] = useState(true);
  const enCurso = useRef(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setVista(null);
    try {
      const { data } = await api.get(`${cfg.base}/vista-previa`, { timeout: 60000 });
      setVista(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [cfg.base]);

  useEffect(() => {
    if (open) {
      setExcluirUrgentes(true);
      cargar();
    }
  }, [open, cargar]);

  const noIncluidas = vista?.no_incluidas?.total || 0;
  const aResolver = Math.max(0, (vista?.total || 0) - noIncluidas - (cfg.conUrgentes && excluirUrgentes ? vista?.urgentes || 0 : 0));

  const confirmar = async () => {
    if (enCurso.current || !vista) return;
    enCurso.current = true;
    setTrabajando(true);
    try {
      const cuerpo = { confirmar: true, esperado: vista.total, corte: vista.corte };
      if (cfg.conUrgentes) cuerpo.excluir_urgentes = excluirUrgentes;
      const { data } = await api.post(cfg.base, cuerpo, { timeout: 120000 });
      toast.success(cfg.hecho(data));
      onDone?.();
      await cargar();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.codigo === "conteo_distinto" ? `Cambió el total (ahora ${n(d.conteo_actual)}). Revisá y confirmá de nuevo.` : formatApiError(d));
      await cargar();
    } finally {
      enCurso.current = false;
      setTrabajando(false);
    }
  };

  const deshacer = async () => {
    if (enCurso.current) return;
    enCurso.current = true;
    setTrabajando(true);
    try {
      const { data } = await api.post(`${cfg.base}/deshacer`, null, { timeout: 120000 });
      toast.success(data.deshecho ? `Lote deshecho: ${n(data.vuelven)} vuelven a pendiente.` : "No hay ningún lote para deshacer.");
      onDone?.();
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      enCurso.current = false;
      setTrabajando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="masivo-dialog">
        <DialogHeader>
          <DialogTitle>{cfg.titulo}</DialogTitle>
          <DialogDescription data-testid="masivo-nota">{cfg.nota}</DialogDescription>
        </DialogHeader>
        {loading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" data-testid="masivo-cargando" />}
        {vista && (
          <div className="space-y-3 text-[13px] text-slate-700">
            <p data-testid="masivo-total">Pendientes en total: <b>{n(vista.total)}</b></p>
            <ul className="text-[12px] text-slate-600" data-testid="masivo-por-tipo">
              {Object.entries(vista.por_tipo || {}).map(([t, c]) => (
                <li key={t} data-testid={`masivo-tipo-${t}`}>{t}: {n(c)}</li>
              ))}
            </ul>
            {cfg.conUrgentes && (
              <>
                <p data-testid="masivo-urgentes">Urgentes (tour de hoy o mañana): <b>{n(vista.urgentes)}</b></p>
                <label className="flex items-center gap-2 text-[12px]">
                  <Checkbox checked={excluirUrgentes} onCheckedChange={(v) => setExcluirUrgentes(v === true)} data-testid="masivo-excluir-urgentes" />
                  No resolver las urgentes
                </label>
                <p data-testid="masivo-no-incluidas">No incluidas: <b>{n(noIncluidas)}</b>{vista.no_incluidas?.motivo ? ` (${vista.no_incluidas.motivo})` : ""}</p>
              </>
            )}
            {vista.ultimo_lote && (
              <Button variant="outline" size="sm" onClick={deshacer} disabled={trabajando} className="gap-2" data-testid="masivo-deshacer-btn">
                <Undo2 className="h-4 w-4" /> Deshacer el último lote
              </Button>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="masivo-cancelar-btn">Cerrar</Button>
          <Button onClick={confirmar} disabled={!vista || trabajando || aResolver === 0} className="gap-2" data-testid="masivo-confirmar-btn">
            {trabajando ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4" />}
            {`Sí, ${cfg.verbo} ${n(aResolver)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Botón con el total del servidor (no 500). Deshabilitado con N=0 o con una búsqueda activa. */
export default function MasivoBoton({ modo, busqueda = "", refresco = 0, onDone }) {
  const cfg = MODOS[modo];
  const [total, setTotal] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const hayBusqueda = !!(busqueda || "").trim();

  useEffect(() => {
    let vivo = true;
    api.get(`${cfg.base}/vista-previa`, { timeout: 60000 })
      .then(({ data }) => vivo && setTotal(data.total))
      .catch(() => vivo && setTotal(null));
    return () => { vivo = false; };
  }, [cfg.base, refresco, abierto]);

  return (
    <div className="flex items-center gap-2" data-testid={`masivo-${modo}`}>
      <Button variant="outline" size="sm" disabled={!total || hayBusqueda} onClick={() => setAbierto(true)} className="gap-2" data-testid={`masivo-${modo}-btn`}>
        <CheckCheck className="h-4 w-4" /> {`${cfg.titulo} (${n(total)})`}
      </Button>
      {hayBusqueda && <span className="text-[11px] text-slate-500" data-testid="masivo-aviso-busqueda">Borrá la búsqueda para usarlo</span>}
      <MasivoDialog open={abierto} onOpenChange={setAbierto} modo={modo} onDone={onDone} />
    </div>
  );
}
